import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { basicAvailableUsdAtom, formatUsdCompact } from '@root/entities/basic'
import { useTranslation } from '@root/shared/localization'
import {
  ChargeExactIcon,
  CheckCircleIcon,
  CrossCircleIcon,
} from '@root/shared/ui-kit'

import { openBasicDepositDialogAction } from '../../basic-deposit'
import {
  basicQuickBuyAmountAtom,
  reatomBasicQuickBuy,
} from '../atoms/reatom-basic-quick-buy'

import type { BasicQuickBuyTarget } from '../atoms/reatom-basic-quick-buy'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  target: BasicQuickBuyTarget
  withLabel?: boolean
  withAmount?: boolean
  withIcon?: boolean
} & ClassNameProps

/** How long the tick or cross stays before the amount comes back. */
const RESET_DELAY = 3000

/**
 * One tap, one buy, in dollars.
 *
 * The amount is on the button, so there is nothing to confirm: pressing "$10"
 * spends ten dollars. When the balance cannot cover it the same tap opens the
 * deposit dialog instead of sending an order the server would refuse.
 */
export const BasicQuickBuyButton: React.FC<Props> = ({
  target,
  withLabel,
  withAmount,
  withIcon,
  className,
}) => {
  const { t } = useTranslation()
  const [amount] = useAtom(basicQuickBuyAmountAtom)
  const [available] = useAtom(basicAvailableUsdAtom)
  const openDeposit = useAction(openBasicDepositDialogAction)

  const request = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule -- named inside, per token
      reatomBasicQuickBuy(target),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one model per token, not per render
    [target.network, target.tokenAddress, target.symbol],
  )
  const [requestState] = useAtom(request)
  const buy = useAction(request.execute)

  // The result shows for a moment and then gives the button back, so the
  // next tap is on an amount and not on a tick.
  const [shown, setShown] =
    useState<AsyncStateN.AsyncStateN<unknown, unknown>>(requestState)
  useEffect(() => {
    setShown(requestState)
    if (AsyncStateN.isReady(requestState)) {
      const timeout = setTimeout(
        () => setShown(AsyncStateN.notInitiated()),
        RESET_DELAY,
      )
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [requestState])

  const spendable = pipe(
    available,
    AsyncStateN.getOrElseW(() => undefined),
  )
  const short =
    amount !== undefined &&
    spendable !== undefined &&
    amount.isGreaterThan(spendable)

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    // Cards and rows are links to the token; a quick buy must not also
    // navigate away from where it was pressed.
    event.preventDefault()
    event.stopPropagation()

    if (short) {
      openDeposit()
      return
    }

    buy()
  }

  const busy = !AsyncStateN.isNotInitiated(shown)

  return (
    <Root
      type="button"
      className={className}
      onClick={handleClick}
      disabled={busy || amount === undefined}
      data-state={AsyncStateN.match(shown, {
        Loading: () => 'loading',
        Success: () => 'success',
        Error: () => 'error',
        NotInitiated: () => 'idle',
      })}
      data-short={short}
      title={short ? t('basic.quick_buy_not_enough') : undefined}
    >
      <Content data-hidden={busy}>
        {withIcon ? <ChargeExactIcon size={14} /> : undefined}
        {withLabel ? <Label>{t('trading.buy')}</Label> : undefined}
        {withAmount && amount ? (
          <Amount>{formatUsdCompact(amount)}</Amount>
        ) : undefined}
      </Content>
      {busy ? (
        <State>
          {AsyncStateN.match(shown, {
            Loading: () => <CircularLoader size={16} />,
            Success: () => <CheckCircleIcon size={16} />,
            Error: () => <CrossCircleIcon size={16} />,
            NotInitiated: () => <></>,
          })}
        </State>
      ) : undefined}
    </Root>
  )
}

const Root = styled.button`
  position: relative;
  pointer-events: auto;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  height: var(--buy-button-height, 28px);
  padding-inline: 8px;

  border: 1px solid var(--primaryA-6);
  border-radius: 6px;
  background: var(--primaryA-3);
  color: var(--primary-11, var(--primary-9));
  transition: background 0.1s ease-in-out;

  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--primaryA-5);
  }
  &:disabled {
    cursor: default;
  }
  &[data-short='true'] {
    opacity: 0.6;
  }
  &[data-state='success'] {
    border-color: var(--successA-3);
    background: var(--successA-2);
    color: var(--success-9);
  }
  &[data-state='error'] {
    border-color: var(--errorA-3);
    background: var(--errorA-2);
    color: var(--error-9);
  }
`

const Content = styled.span`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 3px;

  &[data-hidden='true'] {
    visibility: hidden;
  }
`

const Label = styled.span`
  text-transform: uppercase;
`

const Amount = styled.span`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
`

const State = styled.span`
  position: absolute;
  inset: 0;
  display: grid;
  align-items: center;
  justify-content: center;
`
